import { api } from '~/trpc-extra/api';
import type { TestContentMutationContext } from '../callback-handles';
import { cancelTestContentQueries, handleMutationError, handleMutationSuccess } from '../callback-handles';
import { useLiveStateManager } from '../../useLiveStateManager';
import { isOperationOptimistic, isStepOptimistic, OPTIMISTIC_STEP_SEPARATING_KEY } from '../optimistic-handles';
import { updateLiveStateOnStepRemoval } from '../cursor-correction';

export const useStepOpsToSteps = ({ testContentId, stepId }: { testContentId: string; stepId: string }) => {
  const trpcUtils = api.useUtils();
  const { recorderState, updateLiveState } = useLiveStateManager();

  const stepOpsToStepsMutation = api.testStep.stepOpsToSteps.useMutation<TestContentMutationContext>({
    onSuccess: () =>
      handleMutationSuccess({
        message: 'Step broken into smaller steps',
        trpcUtils,
        testContentId,
      }),
    onError: (_error, _variables, context) =>
      handleMutationError({
        message: 'Failed to break step into smaller steps',
        trpcUtils,
        testContentId,
        context,
      }),
    onMutate: async () => {
      await cancelTestContentQueries({ trpcUtils, testContentId });
      const prevData = trpcUtils.testContent.get.getData({ id: testContentId });
      if (!prevData) {
        return { prevData: null };
      }

      const newData = {
        ...prevData,
        steps: prevData.steps.map((step) =>
          step.id === stepId ?
            {
              ...step,
              id: `${OPTIMISTIC_STEP_SEPARATING_KEY}-${step.id}-${Date.now()}`,
              operations: step.operations?.filter((op) => !isOperationOptimistic(op.id)),
            }
          : step,
        ),
      };

      if (recorderState?.stepId === stepId) {
        const filteredSteps = newData.steps.filter((step) => !isStepOptimistic(step.id));
        updateLiveStateOnStepRemoval({
          steps: filteredSteps,
          recorderState: {
            stepId: recorderState.stepId,
            stepOrder: recorderState.stepOrder,
          },
          updateLiveState,
        });
      }

      trpcUtils.testContent.get.setData({ id: testContentId }, newData);
      return { prevData };
    },
  });

  return stepOpsToStepsMutation;
};
